import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  approveCount,
  cancelCount,
  createCount,
  enterCountLine,
  fetchCount,
  fetchCounts,
  submitCount,
} from '../api'
import type { CreateCountBody, EnterCountLineBody } from '../types'
import { invalidateStock } from './use-adjustments'
import { inventoryKey } from './use-inventory'

const countsKey = [...inventoryKey, 'counts'] as const

/** One page of counts, newest first, for the Counts tab. */
export function useCountList(page: number, pageSize: number) {
  const query = { limit: pageSize, offset: page * pageSize }

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...countsKey, 'list', query],
    queryFn: () => fetchCounts(query),
    placeholderData: (previous) => previous,
  })

  return {
    counts: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

/** One count with its entries — the count sheet. `null` reads nothing. */
export function useCount(id: string | null) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: [...countsKey, 'one', id],
    queryFn: () => fetchCount(id as string),
    enabled: id !== null,
  })

  return {
    count: data ?? null,
    isLoading: id !== null && isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useCountActions() {
  const queryClient = useQueryClient()
  const refreshCounts = () => queryClient.invalidateQueries({ queryKey: countsKey })

  const create = useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: CreateCountBody; idempotencyKey: string }) =>
      createCount(body, idempotencyKey),
    onSuccess: refreshCounts,
  })

  /* A line moves no stock; the sheet and the list pick up its state. */
  const enterLine = useMutation({
    mutationFn: ({
      countId,
      itemId,
      body,
    }: {
      countId: string
      itemId: string
      body: EnterCountLineBody
    }) => enterCountLine(countId, itemId, body),
  })

  type Step = { id: string; idempotencyKey: string }
  /* Submit and approve may post, so the stock is refreshed with the counts. */
  const afterPost = () => Promise.all([refreshCounts(), invalidateStock(queryClient)])

  const submit = useMutation({
    mutationFn: ({ id, idempotencyKey }: Step) => submitCount(id, idempotencyKey),
    onSuccess: afterPost,
  })
  const approve = useMutation({
    mutationFn: ({ id, idempotencyKey }: Step) => approveCount(id, idempotencyKey),
    onSuccess: afterPost,
  })
  const cancel = useMutation({
    mutationFn: ({ id, idempotencyKey }: Step) => cancelCount(id, idempotencyKey),
    onSuccess: refreshCounts,
  })

  return { create, enterLine, submit, approve, cancel, refreshCounts }
}
